import { z } from 'zod';
import { CursorPaginationSchema } from './shared/pagination.js';
import { booleanFromQueryParam } from './shared/query.js';
import { TimeRangeSchema } from './time-range.js';

export const InsightsMetadataSchema = z
  .object({
    dataComplete: z.boolean(),
    oldestDataPoint: z.date().nullable(),
    newestDataPoint: z.date().nullable(),
    needsSync: z.boolean(),
    coveragePercent: z.number(),
    capReached: z.boolean().optional(),
    tweetCount: z.number().optional(),
    maxTweets: z.number().optional(),
    timezone: z.string().optional(),
    totalCharacters: z.number().nullable().optional(),
    totalWords: z.number().nullable().optional(),
    estimatedReadingSeconds: z.number().nullable().optional(),
    averageCharactersPerTweet: z.number().nullable().optional(),
  })
  .optional();

export type InsightsMetadata = z.infer<typeof InsightsMetadataSchema>;

export const InsightsVerifiedTypeSchema = z.enum(['none', 'blue', 'business', 'government']);

export const InsightsEngagementTotalsSchema = z.object({
  total: z.number(),
  replies: z.number(),
  retweets: z.number(),
  mentions: z.number(),
});

export const InsightsUserActivityBucketSchema = z.object({
  start: z.date(),
  end: z.date(),
  original: z.number(),
  replies: z.number(),
  links: z.number(),
  quotes: z.number(),
  total: z.number(),
});

export const InsightsUserFrequencySchema = z.object({
  userId: z.string(),
  username: z.string(),
  name: z.string().optional().nullable(),
  profileImageUrl: z.string().optional().nullable(),
  verifiedType: InsightsVerifiedTypeSchema,
  createdAt: z.date(),
  followersCount: z.number().optional().nullable(),
  followingCount: z.number().optional().nullable(),
  tweetsCount: z.number().optional().nullable(),
  posts: z.number(),
  percentage: z.number(),
  originalPosts: z.number().optional().nullable(),
  replies: z.number().optional().nullable(),
  retweets: z.number().optional().nullable(),
  quotes: z.number().optional().nullable(),
  links: z.number().optional().nullable(),
  yourEngagement: z.object({
    totalInteractions: z.number(),
    inbound: InsightsEngagementTotalsSchema,
    outbound: InsightsEngagementTotalsSchema,
    reciprocity: z.number().nullable(),
    lastInboundAt: z.date().nullable(),
    lastOutboundAt: z.date().nullable(),
  }),
  averageEngagement: z
    .object({
      likes: z.number(),
      retweets: z.number(),
      replies: z.number(),
    })
    .optional(),
  peakPostingHour: z.number().optional().nullable(),
  activity: z
    .object({
      bucketMinutes: z.number(),
      buckets: z.array(InsightsUserActivityBucketSchema),
    })
    .optional(),
});

export type InsightsUserFrequency = z.infer<typeof InsightsUserFrequencySchema>;

export const InsightsSummarySchema = z.object({
  totalPosts: z.number(),
  totalUsers: z.number(),
  averagePostsPerUser: z.number().optional(),
  topPosters: z
    .array(
      z.object({
        username: z.string(),
        posts: z.number(),
        percentage: z.number(),
      })
    )
    .optional(),
  hourlyActivity: z
    .array(
      z.object({
        hour: z.number(),
        posts: z.number(),
      })
    )
    .optional(),
  peakHour: z
    .object({
      hour: z.number(),
      posts: z.number(),
    })
    .optional(),
});

export type InsightsSummary = z.infer<typeof InsightsSummarySchema>;

export const InsightsHistogramBucketSchema = z.object({
  label: z.string(),
  count: z.number(),
});

export const InsightsTopUserMetricSchema = z.object({
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().optional().nullable(),
  metricValue: z.number(),
  percentage: z.number().optional(),
});

export const InsightsFollowedShareSchema = z.object({
  followedCount: z.number(),
  totalAuthors: z.number(),
  percentage: z.number(),
});

export const InsightsRelationshipCountsSchema = z.object({
  mutual: z.number(),
  following: z.number(),
  followedBy: z.number(),
  none: z.number(),
});

export const InsightsEngagementPointSchema = z.object({
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().optional().nullable(),
  posts: z.number(),
  engagement: z.number(),
  followersCount: z.number(),
});

export type InsightsEngagementPoint = z.infer<typeof InsightsEngagementPointSchema>;

export const InsightsFilterCountSchema = z.object({
  isRetweet: z.boolean(),
  isReply: z.boolean(),
  hasLinks: z.boolean(),
  hasMedia: z.boolean(),
  count: z.number(),
});

export type InsightsFilterCount = z.infer<typeof InsightsFilterCountSchema>;

export const InsightsAggregatesSchema = z.object({
  postTypeTotals: z.object({
    original: z.number(),
    links: z.number(),
    replies: z.number(),
    quotes: z.number(),
    totalPosts: z.number(),
    threadPercentage: z.number(),
  }),
  filterCounts: z.array(InsightsFilterCountSchema).optional(),
  topAuthors: z
    .array(
      z.object({
        username: z.string(),
        name: z.string(),
        profileImageUrl: z.string().optional().nullable(),
        posts: z.number(),
        percentage: z.number(),
      })
    )
    .optional(),
  topLinkSharer: InsightsTopUserMetricSchema.optional(),
  topReplier: InsightsTopUserMetricSchema.optional(),
  newestProfile: z
    .object({
      username: z.string(),
      name: z.string(),
      profileImageUrl: z.string().optional().nullable(),
      joinedAt: z.date(),
    })
    .optional(),
  mostFollowers: InsightsTopUserMetricSchema.optional(),
  mostFollowing: InsightsTopUserMetricSchema.optional(),
  averageEngagementPerUser: z.number(),
  followerHistogram: z.array(InsightsHistogramBucketSchema),
  engagementScatter: z.array(InsightsEngagementPointSchema),
  followedShare: InsightsFollowedShareSchema,
  relationshipCounts: InsightsRelationshipCountsSchema,
});

export type InsightsAggregates = z.infer<typeof InsightsAggregatesSchema>;

const InsightsPaginationSchema = CursorPaginationSchema.extend({
  limit: z.number().int().min(0).max(200).nullable().optional(),
  offset: z.number().int().min(0).nullable().optional(),
  totalCount: z.number().int().nonnegative().nullable().optional(),
});

export const InsightsResponseSchema = z.object({
  summary: InsightsSummarySchema.optional(),
  userFrequencyPage: z.object({
    items: z.array(InsightsUserFrequencySchema),
    pagination: InsightsPaginationSchema,
  }),
  aggregates: InsightsAggregatesSchema.optional(),
  dataSource: z.string().optional(),
  metadata: InsightsMetadataSchema,
  timezone: z.string().optional(),
});

export type InsightsResponse = z.infer<typeof InsightsResponseSchema>;

export const InsightsSortFieldSchema = z.enum([
  'user',
  'posts',
  'percentage',
  'originalPosts',
  'links',
  'replies',
  'quotes',
  'interactions',
]);

export type InsightsSortField = z.infer<typeof InsightsSortFieldSchema>;

export const InsightsSortDirectionSchema = z.enum(['asc', 'desc']);

export type InsightsSortDirection = z.infer<typeof InsightsSortDirectionSchema>;

export const TimelineInsightsQuerySchema = z.object({
  timeRange: TimeRangeSchema.optional().default('24h'),
  includeRetweets: booleanFromQueryParam.default(true),
  includeReplies: booleanFromQueryParam.default(true),
  minPosts: z.coerce.number().min(1).optional().default(1),
  cursor: z.string().optional(),
  limit: z.coerce.number().min(0).max(200).optional().default(25),
  sortField: InsightsSortFieldSchema.optional().default('posts'),
  sortDirection: InsightsSortDirectionSchema.optional().default('desc'),
  timezone: z.string().optional(),
});

export type TimelineInsightsQuery = z.infer<typeof TimelineInsightsQuerySchema>;

export const TimelineInsightsBodySchema = z.object({
  timeRange: TimeRangeSchema.optional().default('24h'),
  includeRetweets: z.boolean().optional().default(true),
  includeReplies: z.boolean().optional().default(true),
  minPosts: z.number().min(1).optional().default(1),
  specificUsers: z.array(z.string()).nullable().optional().default(null),
  sortBy: z.enum(['posts', 'percentage', 'engagement']).optional().default('posts'),
  sortOrder: InsightsSortDirectionSchema.optional().default('desc'),
  timezone: z.string().optional(),
});

export type TimelineInsightsBody = z.infer<typeof TimelineInsightsBodySchema>;

export const TimelineSummaryTotalsSchema = z.object({
  totalPosts: z.number(),
  links: z.number(),
  replies: z.number(),
  quotes: z.number(),
  original: z.number(),
  retweets: z.number(),
  filterCounts: z.array(InsightsFilterCountSchema).default([]),
});

export type TimelineSummaryTotals = z.infer<typeof TimelineSummaryTotalsSchema>;
